import React, { useMemo } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";

import { useGetAllStores } from "src/utils/hooks/options";

import { getDefaultDateRange, validateDateRange } from "./dashboardUtils";

export default function DashboardToolbar({
  periodType,
  setPeriodType,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectedStoreCodes,
  setSelectedStoreCodes,
  onRefresh,
  isRefreshing,
}) {
  const { stores } = useGetAllStores();

  const fontStyle = { fontFamily: "'Inter', sans-serif" };

  // Khi bấm đổi loại kỳ (ngày / tháng / năm)
  const handlePeriodTypeChange = (newType) => {
    setPeriodType(newType);
    const defaults = getDefaultDateRange(newType);
    setFromDate(defaults.fromDate);
    setToDate(defaults.toDate);
  };

  // Đặt lại khoảng ngày mặc định chuẩn
  const handleResetDefaults = () => {
    const defaults = getDefaultDateRange(periodType);
    setFromDate(defaults.fromDate);
    setToDate(defaults.toDate);
    toast.info("Đã đặt lại khoảng thời gian.");
  };

  // Kiểm tra tính hợp lệ của ngày nhập
  const validation = useMemo(() => {
    return validateDateRange(periodType, fromDate, toDate);
  }, [periodType, fromDate, toDate]);

  return (
    <div
      className="!font-inter !bg-white !rounded-2xl !p-4 md:!p-5 !shadow-xs !border !border-slate-200/90 !space-y-4"
      style={fontStyle}
    >
      <div
        className="!flex !flex-col lg:!flex-row !gap-4 !items-start lg:!items-center !justify-between"
        style={fontStyle}
      >
        {/* Tiêu đề & Chọn loại kỳ */}
        <div
          className="!flex !flex-wrap !items-center !gap-3.5"
          style={fontStyle}
        >
          <div>
            <h2
              className="!text-base sm:!text-lg !font-extrabold !text-slate-800 !leading-tight"
              style={fontStyle}
            >
              Thống Kê Tồn Kho & Mua Hàng
            </h2>
            <p
              className="!text-sm !font-medium !text-slate-500 !mt-0.5"
              style={fontStyle}
            >
              Số liệu theo kỳ và định mức chi nhánh
            </p>
          </div>

          {/* Chọn loại kỳ (Theo Ngày / Theo Tháng / Theo Năm) */}
          <div
            className="!inline-flex !p-1 !bg-slate-100/90 !rounded-xl !border !border-slate-200/80 !text-sm !font-semibold"
            style={fontStyle}
          >
            <button
              onClick={() => handlePeriodTypeChange("day")}
              className={`!px-3.5 !py-1.5 !rounded-lg !transition-all !flex !items-center !gap-1.5 ${
                periodType === "day"
                  ? "!bg-[#053c7f] !text-white !shadow-xs !font-bold"
                  : "!text-slate-600 hover:!text-slate-900 hover:!bg-white/60"
              }`}
              style={fontStyle}
            >
              <span>Theo Ngày</span>
              <span
                className={`!text-sm !px-1.5 !rounded-md !font-medium ${
                  periodType === "day"
                    ? "!bg-white/20 !text-white"
                    : "!bg-blue-50 !text-blue-600"
                }`}
                style={fontStyle}
              >
                (tối đa 7 ngày)
              </span>
            </button>

            <button
              onClick={() => handlePeriodTypeChange("month")}
              className={`!px-3.5 !py-1.5 !rounded-lg !transition-all !flex !items-center !gap-1.5 ${
                periodType === "month"
                  ? "!bg-[#053c7f] !text-white !shadow-xs !font-bold"
                  : "!text-slate-600 hover:!text-slate-900 hover:!bg-white/60"
              }`}
              style={fontStyle}
            >
              <span>Theo Tháng</span>
              <span
                className={`!text-sm !px-1.5 !rounded-md !font-medium ${
                  periodType === "month"
                    ? "!bg-white/20 !text-white"
                    : "!bg-blue-50 !text-blue-600"
                }`}
                style={fontStyle}
              >
                (tối đa 6 tháng)
              </span>
            </button>

            <button
              onClick={() => handlePeriodTypeChange("year")}
              className={`!px-3.5 !py-1.5 !rounded-lg !transition-all !flex !items-center !gap-1.5 ${
                periodType === "year"
                  ? "!bg-[#053c7f] !text-white !shadow-xs !font-bold"
                  : "!text-slate-600 hover:!text-slate-900 hover:!bg-white/60"
              }`}
              style={fontStyle}
            >
              <span>Theo Năm</span>
              <span
                className={`!text-sm !px-1.5 !rounded-md !font-medium ${
                  periodType === "year"
                    ? "!bg-white/20 !text-white"
                    : "!bg-blue-50 !text-blue-600"
                }`}
                style={fontStyle}
              >
                (tối đa 6 năm)
              </span>
            </button>
          </div>
        </div>

        {/* Bộ lọc ngày, chi nhánh & nút làm mới */}
        <div
          className="!flex !flex-wrap !items-center !gap-3 !w-full lg:!w-auto"
          style={fontStyle}
        >
          {/* Từ ngày */}
          <div className="!flex !items-center !gap-2" style={fontStyle}>
            <span
              className="!text-sm !font-semibold !text-slate-600"
              style={fontStyle}
            >
              Từ:
            </span>
            <input
              type="date"
              value={fromDate}
              max={toDate || dayjs().format("YYYY-MM-DD")}
              onChange={(e) => setFromDate(e.target.value)}
              className="!text-sm !font-semibold !text-slate-700 !bg-white !border !border-slate-300 !rounded-xl !px-3 !py-1.5 focus:!outline-none focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100 !transition-all"
              style={fontStyle}
            />
          </div>

          {/* Đến ngày */}
          <div className="!flex !items-center !gap-2" style={fontStyle}>
            <span
              className="!text-sm !font-semibold !text-slate-600"
              style={fontStyle}
            >
              Đến:
            </span>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              max={dayjs().format("YYYY-MM-DD")}
              onChange={(e) => setToDate(e.target.value)}
              className="!text-sm !font-semibold !text-slate-700 !bg-white !border !border-slate-300 !rounded-xl !px-3 !py-1.5 focus:!outline-none focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100 !transition-all"
              style={fontStyle}
            />
          </div>

          {/* Chọn chi nhánh */}
          <div
            className="!flex !items-center !gap-2 !min-w-[190px]"
            style={fontStyle}
          >
            <select
              value={
                selectedStoreCodes.length === 0
                  ? "all"
                  : selectedStoreCodes.length === 1
                    ? selectedStoreCodes[0]
                    : "custom"
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === "all") {
                  setSelectedStoreCodes([]);
                } else if (val !== "custom") {
                  setSelectedStoreCodes([val]);
                }
              }}
              className="!text-sm !font-semibold !text-slate-700 !bg-white !border !border-slate-300 !rounded-xl !px-3 !py-1.5 focus:!outline-none focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100 !transition-all !w-full"
              style={fontStyle}
            >
              <option value="all" style={fontStyle}>
                Tất cả chi nhánh ({stores.length})
              </option>
              {stores.map((s) => (
                <option key={s.value} value={s.value} style={fontStyle}>
                  {s.label} ({s.value})
                </option>
              ))}
            </select>
          </div>

          {/* Nút đặt lại mặc định */}
          <button
            onClick={handleResetDefaults}
            className="!px-3.5 !py-1.5 !bg-white hover:!bg-slate-50 !text-slate-700 !text-sm !font-semibold !rounded-xl !transition-all !border !border-slate-300 !shadow-2xs"
            style={fontStyle}
          >
            Mặc định
          </button>

          {/* Nút Làm mới */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="!inline-flex !items-center !gap-2 !px-4 !py-1.5 !bg-[#053c7f] hover:!bg-[#074b9e] disabled:!bg-blue-300 !text-white !text-sm !font-bold !rounded-xl !shadow-xs !transition-all"
            style={fontStyle}
          >
            <svg
              className={`!w-4 !h-4 ${isRefreshing ? "!animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>{isRefreshing ? "Đang tải..." : "Làm mới"}</span>
          </button>
        </div>
      </div>

      {/* Cảnh báo khoảng ngày */}
      {!validation.valid && (
        <div
          className="!flex !items-center !gap-2 !p-3 !bg-amber-50 !border !border-amber-200 !rounded-xl !text-sm !text-amber-800"
          style={fontStyle}
        >
          <svg
            className="!w-4 !h-4 !text-amber-600 !shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="!font-medium">{validation.error}</span>
        </div>
      )}
    </div>
  );
}
